import { useContext, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import { CurrentDataContext } from 'src/app/current-data';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { StatsigFeatureKeys } from 'src/features';
import { useUserWorkspacePref } from 'src/hooks/use-pref';
import { getisUnlinkedWsFlagNotificationRelevant } from 'src/selectors/global-selectors';
import {
  publishScreenEvent,
  publishUiEvent,
} from 'src/utils/analytics/publish';
import { captureExceptionWithTags } from 'src/utils/sentry';

import { UnlinkedWorkspaceFeaturelistModal } from './modals/unlinked-workspace-featurelist-modal';
import { UnlinkedWorkspaceFeedbackCollector } from './unlinked-workspace-feedback-collector';
import { checkIfWorkspaceLinked } from './unlinked-workspace-notifications';

const MODAL_RECURRENCE_TIME_MS = 1000 * 60 * 60 * 48; // 48 hours

export const checkIfDismissedRecently = (dismissedAt: Date | null) => {
  if (!dismissedAt) {
    return false;
  }

  try {
    const now = new Date().getTime();
    const timeSinceDismissed = now - dismissedAt.getTime();

    return timeSinceDismissed < MODAL_RECURRENCE_TIME_MS;
  } catch (error) {
    captureExceptionWithTags(error);
    return true;
  }
};

const useInteractionState = () => {
  const USER_PREFS_KEY = 'unlinked-ws-messaging-interactions';

  const [interactionStateJson, setInteractionState, isLoadingInteractionState] =
    useUserWorkspacePref(USER_PREFS_KEY);

  let interactionState: Record<string, string> = {};

  try {
    interactionState = JSON.parse(interactionStateJson || '{}');
  } catch (error) {
    captureExceptionWithTags(error);
    interactionState = {};
  }

  if (typeof interactionState !== 'object' || Array.isArray(interactionState)) {
    interactionState = {};
  }

  const setSubmittedFeedbackAt = (date: Date) => {
    const updatedState = {
      ...interactionState,
      submittedFeedbackAt: date.toISOString(),
    };
    setInteractionState(JSON.stringify(updatedState));
  };

  const setFeatureModalDismissedAt = (date: Date) => {
    const updatedState = {
      ...interactionState,
      featureModalDismissedAt: date.toISOString(),
    };
    setInteractionState(JSON.stringify(updatedState));
  };

  return {
    isLoadingInteractionState,
    submittedFeedbackAt: interactionState.submittedFeedbackAt
      ? new Date(interactionState.submittedFeedbackAt)
      : null,
    featureModalDismissedAt: interactionState.featureModalDismissedAt
      ? new Date(interactionState.featureModalDismissedAt)
      : null,
    setSubmittedFeedbackAt,
    setFeatureModalDismissedAt,
  };
};

export const UnlinkedWorkspaceFeaturelistMsgWrapper = () => {
  const [showModal, setShowModal] = useState(false);
  const {
    isLoadingInteractionState,
    submittedFeedbackAt,
    featureModalDismissedAt,
    setSubmittedFeedbackAt,
    setFeatureModalDismissedAt,
  } = useInteractionState();
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  // isTaskComplete ensures that we don't even try to execute the conditional logic for showing the modal
  // if the user has dismissed the modal in this session
  const [isTaskComplete, setTaskComplete] = useState(false);

  const { workspace: currentWorkspace } = useContext(CurrentDataContext);
  const isNotificationRelevant = useSelector(
    getisUnlinkedWsFlagNotificationRelevant
  );

  const sentFeedback = submittedFeedbackAt !== null;
  const dismissedRecently = checkIfDismissedRecently(featureModalDismissedAt);

  useEffect(() => {
    if (
      isTaskComplete ||
      !isNotificationRelevant ||
      sentFeedback ||
      dismissedRecently ||
      isLoadingInteractionState
    ) {
      setShowModal(false);
      return;
    }

    const checkStatusAndShowOrHideNotification = async () => {
      const isWorkspaceLinked = await checkIfWorkspaceLinked(
        currentWorkspace?.slug
      );

      if (isWorkspaceLinked === false) {
        setShowModal(true);
        publishScreenEvent('unlinked-ws-featurelist-modal-shown');
      } else {
        setTaskComplete(true);
      }
    };

    checkStatusAndShowOrHideNotification();
  }, [
    currentWorkspace,
    isNotificationRelevant,
    sentFeedback,
    dismissedRecently,
    isLoadingInteractionState,
    isTaskComplete,
  ]);

  if (isTaskComplete) {
    return null;
  }

  if (showFeedbackModal) {
    const onCancelFeedback = () => {
      publishUiEvent({
        action: 'clicked',
        actionSubject: 'button',
        actionSubjectId: 'cancel-feedback-unlinked-ws-featurelist-msg',
        source: 'unlinkedWorkspaceFeaturelistMsgFeedbackModal',
      });

      setShowFeedbackModal(false);
      setTaskComplete(true);
    };

    const onSubmitFeedback = () => {
      publishUiEvent({
        action: 'submitted',
        actionSubject: 'form',
        actionSubjectId: 'submit-feedback-unlinked-ws-featurelist-msg',
        source: 'unlinkedWorkspaceFeaturelistMsgFeedbackModal',
      });

      setShowFeedbackModal(false);
      setSubmittedFeedbackAt(new Date());
      setTaskComplete(true);
    };

    return (
      <UnlinkedWorkspaceFeedbackCollector
        onCancel={onCancelFeedback}
        onSubmit={onSubmitFeedback}
      />
    );
  }

  if (!showModal) {
    return null;
  }

  const onLinkWsClick = () => {
    setTaskComplete(true);
    setFeatureModalDismissedAt(new Date());
    setShowModal(false);

    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'linkWorkspace-unlinked-ws-featurelist-msg',
      source: 'unlinkedWorkspaceFeaturelistMsg',
    });
  };

  const onFeedbackLinkClick = () => {
    setShowModal(false);
    setShowFeedbackModal(true);
    setFeatureModalDismissedAt(new Date());

    publishUiEvent({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'share-feedback-unlinked-ws-featurelist-msg',
      source: 'unlinkedWorkspaceFeaturelistMsg',
    });
  };

  const onClose = () => {
    setTaskComplete(true);
    setFeatureModalDismissedAt(new Date());
    setShowModal(false);
  };

  return (
    <UnlinkedWorkspaceFeaturelistModal
      onLinkWsClick={onLinkWsClick}
      onFeedbackLinkClick={onFeedbackLinkClick}
      onClose={onClose}
    />
  );
};

export const UnlinkedWorkspaceFeaturelistMsg = () => {
  const gateIsEnabled = useStatsigGate(
    StatsigFeatureKeys.bbcUnlinkedWsPageBlock
  );

  if (!gateIsEnabled) {
    return null;
  }

  return <UnlinkedWorkspaceFeaturelistMsgWrapper />;
};
